import React, { useMemo } from 'react';
import { Box, TextV2, Flex, ButtonV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { CheckOutline } from '@withjoy/joykit/icons';
import { ReactComponent as Warning } from '@assets/icons/Error-32.svg';
import type { PayoutStatus, PayoutMethod } from './PayoutDialog.types';
import { calcExpectedArrivalDate } from './payoutFeeUtils';
import { pxToRem } from '@withjoy/joykit/theme';

type StatusPayoutDialogProps = Readonly<{
  onClose: () => void;
  status: PayoutStatus;
  selectedMethod: PayoutMethod;
  onDone?: (status: PayoutStatus) => void;
}>;

export const StatusPayoutDialog: React.FC<StatusPayoutDialogProps> = ({ onClose, status, selectedMethod, onDone }) => {
  const { tq } = useTranslation('cashFundsAdmin');
  const isSuccess = status !== 'error';

  const expectedArrivalDate = useMemo(() => (status === 'success' ? calcExpectedArrivalDate(selectedMethod) : null), [status, selectedMethod]);

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      flex={1}
    >
      <TextV2
        textAlign="center"
        fontSize={pxToRem(28)}
        fontWeight={700}
        color="mono14"
        letterSpacing="-0.024em"
        lineHeight="1.2"
        marginBottom={6}
      >
        {tq`Transfer to Bank`}
      </TextV2>

      <Flex
        flexDirection="column"
        alignItems="center"
        flex={1}
        justifyContent="center"
      >
        <Box
          borderStyle="solid"
          borderWidth="2px"
          borderColor={isSuccess ? 'mono13' : 'negative5'}
          borderRadius="50%"
          width={pxToRem(84)}
          height={pxToRem(84)}
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginBottom={isSuccess ? pxToRem(34) : 6}
          flexShrink={0}
        >
          {isSuccess ? (
            <Box
              width={pxToRem(40)}
              height={pxToRem(40)}
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="mono13"
            >
              <CheckOutline />
            </Box>
          ) : (
            <Box
              width={pxToRem(40)}
              height={pxToRem(40)}
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="negative5"
            >
              <Warning />
            </Box>
          )}
        </Box>

        {isSuccess ? (
          <>
            <TextV2
              textAlign="center"
              fontSize={pxToRem(24)}
              fontWeight={700}
              color="mono14"
              letterSpacing="-0.024em"
              lineHeight="1.3"
              marginBottom={pxToRem(14)}
            >
              {tq`It's on its way.`}
            </TextV2>

            {expectedArrivalDate && (
              <TextV2
                textAlign="center"
                fontSize={pxToRem(15)}
                color="mono8"
                marginBottom={pxToRem(14)}
              >
                {tq`Expected to arrive by`}{' '}
                <Box
                  as="span"
                  color="mono14"
                  fontWeight={600}
                >
                  {expectedArrivalDate}
                </Box>
                {'.'}
              </TextV2>
            )}

            <TextV2
              textAlign="center"
              fontSize={pxToRem(15)}
              color="mono8"
              lineHeight="1.5"
              maxWidth={pxToRem(340)}
            >
              {tq`You'll receive an email from Stripe when your payment has processed.`}
            </TextV2>
          </>
        ) : (
          <TextV2
            textAlign="center"
            fontSize={pxToRem(15)}
            color="mono10"
            lineHeight="1.5"
            marginTop={3}
          >
            {tq`We couldn't complete your transfer. Please contact support.`}
          </TextV2>
        )}
      </Flex>

      <ButtonV2
        onClick={() => {
          onDone?.(status);
          onClose();
        }}
        size="lg"
        intent="neutral"
        shape="rounded"
        fullWidth
        marginTop={6}
      >
        {tq`Done`}
      </ButtonV2>
    </Flex>
  );
};
